/**
 * Device Detection Utility
 * Detects device capabilities and iOS versions to prevent crashes
 * @author owendixon
 */

class DeviceDetection {
    constructor() {
        this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        this.isOldIOS = false;
        this.iosVersion = null;
        this.supportsWebGL = null;
        this.isMobile = window.matchMedia('(max-width: 767px)').matches;
        this.isTablet = window.matchMedia('(min-width: 768px) and (max-width: 1023px)').matches;
        this.isLowEndDevice = false;
        
        this.init();
    }
    
    init() {
        // Detect iOS version
        if (this.isIOS) {
            const match = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
            if (match) {
                this.iosVersion = parseInt(match[1], 10);
                // iOS 17 and below can have issues with WebGL + heavy animations
                this.isOldIOS = this.iosVersion <= 17;
            }
        }
        
        // Check WebGL support
        this.supportsWebGL = this.checkWebGLSupport();
        
        // Detect low-end devices
        this.isLowEndDevice = this.detectLowEndDevice();
    }
    
    /**
     * Check if browser supports WebGL
     * @returns {boolean}
     */
    checkWebGLSupport() {
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            
            if (!gl) {
                return false;
            }
            
            // On iOS, check if we can actually create a texture (more thorough test)
            if (this.isIOS) {
                const texture = gl.createTexture();
                if (!texture) {
                    return false;
                }
                gl.deleteTexture(texture);
            }
            
            return true;
        } catch (e) {
            return false;
        }
    }
    
    /**
     * Detect low-end devices that might struggle with heavy animations
     * @returns {boolean}
     */
    detectLowEndDevice() {
        // Check for low memory (if available)
        if (navigator.deviceMemory && navigator.deviceMemory < 4) {
            return true;
        }
        
        // Check for low CPU cores
        if (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4) {
            return true;
        }
        
        // Check for iPhone 12 and older on iOS 17
        if (this.isIOS && this.iosVersion === 17) {
            // iPhone 12 user agent detection
            const isIPhone12OrOlder = /iPhone(10|11|12)/.test(navigator.userAgent);
            if (isIPhone12OrOlder) {
                return true;
            }
        }
        
        // Check for reduced motion preference (often indicates performance concerns)
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return true;
        }
        
        return false;
    }
    
    /**
     * Check if device should disable heavy animations
     * @returns {boolean}
     */
    shouldDisableHeavyAnimations() {
        return this.isLowEndDevice || (this.isIOS && this.iosVersion <= 17);
    }
    
    /**
     * Check if device should disable WebGL effects
     * @returns {boolean}
     */
    shouldDisableWebGL() {
        if (!this.supportsWebGL) {
            return true;
        }
        
        // Disable WebGL on iOS 17 and below for safety
        if (this.isIOS && this.iosVersion <= 17) {
            return true;
        }
        
        // Disable on low-end devices
        if (this.isLowEndDevice) {
            return true;
        }
        
        return false;
    }
    
    /**
     * Get recommended animation settings based on device capabilities
     * @returns {Object}
     */
    getAnimationSettings() {
        return {
            enableWebGL: !this.shouldDisableWebGL(),
            enableParallax: !this.shouldDisableHeavyAnimations(),
            enableHeavyTransforms: !this.isLowEndDevice,
            reduceMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
            maxFPS: this.isLowEndDevice ? 30 : 60
        };
    }
}

// Create global instance
window.pegasusDeviceDetection = new DeviceDetection();

export default DeviceDetection;
